import React from "react";
import useCollision from "./useCollision";

/*
 * Kuest popover (components/ui/popover.tsx): w-72 rounded-md border bg-popover p-4
 * text-sm shadow-md, 4px offset, menu open/close animation. Esc closes and returns
 * focus to the trigger; clicking outside closes.
 */
export const popoverPanelClass = "rounded-md border border-border bg-popover p-4 text-sm text-popover-foreground shadow-md outline-none";

export default function Popover({ trigger, children, align = "end", className = "", label, open: openProp, onOpenChange }) {
  const [inner, setInner] = React.useState(false);
  const controlled = typeof openProp === "boolean";
  const open = controlled ? openProp : inner;
  const setOpen = React.useCallback(
    (next) => {
      if (!controlled) setInner(next);
      if (onOpenChange) onOpenChange(next);
    },
    [controlled, onOpenChange],
  );
  const wrapperRef = React.useRef(null);
  const triggerRef = React.useRef(null);
  const panelId = React.useId();
  const panelRef = React.useRef(null);
  useCollision(panelRef, open, align);

  React.useEffect(() => {
    if (!open) return undefined;
    function onDown(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open, setOpen]);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div ref={wrapperRef} className="relative inline-flex">
      {trigger({
        open,
        props: {
          ref: triggerRef,
          "aria-haspopup": "dialog",
          "aria-expanded": open,
          "aria-controls": open ? panelId : undefined,
          "data-state": open ? "open" : "closed",
          onClick: () => setOpen(!open),
        },
      })}
      {open && (
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-label={label}
          data-state="open"
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.stopPropagation();
              close();
            }
          }}
          className={`wasaa-menu absolute top-[calc(100%+4px)] z-[70] w-72 max-w-[calc(100vw-2rem)] ${
            align === "end" ? "right-0 origin-top-right" : "left-0 origin-top-left"
          } ${popoverPanelClass} ${className}`}
        >
          {typeof children === "function" ? children({ close }) : children}
        </div>
      )}
    </div>
  );
}
